import cn from 'classnames';
import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VoiceSearchButton from 'src/components/base-components/Search/components/VoiceSearchButton';
import GameCard from 'src/components/common/GameCard';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { useDebounce } from 'src/hooks/use-debounce';
import { useOutsideClick } from 'src/hooks/use_outside_click';
import { MediaManager } from 'src/manager/MediaManager';
import { seacrhByGameNameRequest } from 'src/services/gameApi';

const MIN_QUERY_LENGTH = 3;
// keep in sync with the grid-template-columns in styles.scss
const GRID_COLUMNS = 5;

// In-game search: lets the player look up and switch to another game while a game is open.
// A selected result launches through GameCard, which swaps the game inside the open GameBox.
const GameBoxSearch: FC = () => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const latestQueryRef = useRef<string>('');
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [games, setGames] = useState<any[] | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const debouncedSearch = useDebounce(search, 500);

  const resetSearch = (): void => {
    setSearch('');
    latestQueryRef.current = '';
    setGames(null);
    setIsSubmitted(false);
    setIsLoading(false);
  };

  useEffect(() => {
    const term = search.trim();
    const debounced = debouncedSearch.trim();

    if (!term) {
      latestQueryRef.current = '';
      setGames(null);
      setIsSubmitted(false);
      setIsLoading(false);
      return;
    }
    // only fire once the debounced value has caught up with the current input
    if (debounced !== term) return;

    if (debounced.length >= MIN_QUERY_LENGTH) {
      latestQueryRef.current = debounced;
      setIsLoading(true);
      // search globally (no provider/category filter) so any game is reachable in-game
      seacrhByGameNameRequest('desktop', debounced, null, null)
        .then((res: any) => {
          // ignore late responses from stale queries (input changed meanwhile)
          if (latestQueryRef.current !== debounced) return;
          setGames(res?.games ?? []);
          setIsSubmitted(true);
          setIsLoading(false);
        })
        .catch(() => {
          if (latestQueryRef.current !== debounced) return;
          setGames([]);
          setIsSubmitted(true);
          setIsLoading(false);
        });
    } else {
      latestQueryRef.current = '';
      setGames([]);
      setIsSubmitted(false);
      setIsLoading(false);
    }
  }, [debouncedSearch, search]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // clear the keyboard selection whenever the result set changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [games]);

  // keep the keyboard-highlighted card in view
  useEffect(() => {
    if (activeIndex >= 0) {
      cardRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const openSearch = (): void => setIsOpen(true);

  const closeSearch = (): void => {
    setIsOpen(false);
    resetSearch();
  };

  const clearInput = (): void => {
    resetSearch();
    inputRef.current?.focus();
  };

  const onChange = (ev: ChangeEvent<HTMLInputElement>): void => setSearch(ev.target.value);

  const handleVoiceResult = (transcript: string): void => setSearch(transcript);

  // launch a result by triggering its GameCard click (reuses all launch guards)
  const launchGame = (index: number): void => {
    const clickable = cardRefs.current[index]?.querySelector<HTMLElement>('.game') ?? cardRefs.current[index];
    clickable?.click();
  };

  const onInputKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      closeSearch();
      return;
    }

    const count = games?.length ?? 0;
    if (!count) return;

    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        setActiveIndex((i) => (i < 0 ? 0 : Math.min(count - 1, i + GRID_COLUMNS)));
        break;
      case 'ArrowUp':
        ev.preventDefault();
        setActiveIndex((i) => (i < 0 ? 0 : Math.max(0, i - GRID_COLUMNS)));
        break;
      case 'ArrowRight':
        ev.preventDefault();
        setActiveIndex((i) => Math.min(count - 1, i + 1));
        break;
      case 'ArrowLeft':
        ev.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Enter':
        ev.preventDefault();
        launchGame(activeIndex >= 0 ? activeIndex : 0);
        break;
    }
  };

  useOutsideClick(wrapperRef, closeSearch, null, null);

  const hasQuery = search.trim().length >= MIN_QUERY_LENGTH;

  return (
    <div ref={wrapperRef} className={cn('gamebox-search', { ['gamebox-search--open']: isOpen })}>
      {isOpen ? (
        <div className="gamebox-search__field">
          <SvgIcon
            src={MediaManager.getSrcFromMediaAssets('search.svg', '/icons/svg/')}
            className="gamebox-search__field-icon"
          />
          <input
            ref={inputRef}
            type="text"
            className="gamebox-search__input"
            value={search}
            onChange={onChange}
            onKeyDown={onInputKeyDown}
            placeholder={t('searchGame')}
            autoComplete="off"
          />
          {!!search.trim() && (
            <button className="gamebox-search__clear" onClick={clearInput} aria-label={t('search')}>
              <SvgIcon src={icons.close} className="gamebox-search__clear-icon" />
            </button>
          )}
          <VoiceSearchButton onResult={handleVoiceResult} className="gamebox-search__voice" />
        </div>
      ) : (
        <button className="gamebox-search__toggle" onClick={openSearch} aria-label={t('search')}>
          <SvgIcon
            src={MediaManager.getSrcFromMediaAssets('search.svg', '/icons/svg/')}
            className="gamebox-search__toggle-icon"
          />
        </button>
      )}

      {isOpen && hasQuery && (isLoading || isSubmitted) && (
        <div className="gamebox-search__results">
          {isLoading ? (
            <div className="gamebox-search__loader">
              <span className="gamebox-search__spinner" />
            </div>
          ) : (
            <>
              <p className="gamebox-search__results-title">
                <span>{games?.length ?? 0}</span> {t('searchResults')}
              </p>
              {games?.length ? (
                // any click on a launched result also dismisses the search overlay
                <div className="gamebox-search__grid" onClick={closeSearch}>
                  {games.map((game: any, index: number) => (
                    <div
                      className={cn('gamebox-search__card', {
                        ['gamebox-search__card--active']: index === activeIndex,
                      })}
                      key={game.id}
                      ref={(el) => {
                        cardRefs.current[index] = el;
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <GameCard
                        data={{
                          thumbnail: game.thumbnail,
                          name: game.name,
                          providerName: game.providerName,
                          id: game.id,
                          cols: 1,
                          rows: 1,
                        }}
                        size="sm"
                        effects={{ hoverType: '', src: '', text: '' }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="gamebox-search__empty">
                  <p className="gamebox-search__empty-title">{t('noResultFoundTitle')}</p>
                  <p className="gamebox-search__empty-text">{t('noResultFoundText')}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GameBoxSearch;
